import React from "react";
import { selectors } from "@Src/redux/reducers";
import { useSelector, useDispatch } from "react-redux";
import { Creators } from "@Src/redux/actionTypes";
import { getQueryParams } from "@Src/utils/UrlUtility";
import {
  ROUTES,
  Member,
  DEALS_SSO_UC1_TOKEN_PARAMS,
  DEALS_SSO_UC2_TOKEN_PARAMS,
  SECURE_LINK_AUTH_TOKEN_PARAMS,
} from "@Src/constants";
import { removeCookie } from "@Src/utils/cookie.utils";
import Loading from "@Components/loading";

const { getAuthenticationStatus } = selectors;
const readyToContinueStatuses = [
  Member.AUTHENTICATION_FAILURE,
  Member.AUTHENTICATION_SUCCESSFUL,
  Member.SIGN_IN_SUCCESSFUL,
];

const CheckForSession = ({ children }) => {
  const params = getQueryParams();
  const programKey = useSelector(selectors.getProgramKey);
  const memberAuthStatus = useSelector(getAuthenticationStatus);
  const registrationType = useSelector(selectors.getRegistrationType);
  const isSlaTokenProgram = useSelector(selectors.getIsSlaTokenEnabled);
  const [readyToContinue, setReadyToContinue] = React.useState(false);
  const isDirectorRoute = window.location.pathname === ROUTES.DIRECTOR_PATH;
  const dispatch = useDispatch();

  const isSsoRegType = registrationType === Member.DEALS_SSO;

  const testForCvt = React.useCallback(() => {
    // if SLA token program, cvt auth not allowed
    if (isSlaTokenProgram) return false;

    // list of SSO programs that allow cvt auth
    const cvtAllowedSsoPrograms = ["202734"]; // RaiseRight stage
    // if SSO program AND not allowed SSO program, cvt auth not allowed
    if (isSsoRegType && !cvtAllowedSsoPrograms.includes(programKey)) return false;

    // if cvt present & neither of the above cases, cvt auth is allowed
    if (params.hasOwnProperty("cvt")) {
      return true;
    }

    return false;
  }, [params, programKey, isSsoRegType, isSlaTokenProgram]);

  const testForSsoUc1Tokens = React.useCallback(() => {
    if (!isSsoRegType) return false;
    const hasAllParams = DEALS_SSO_UC1_TOKEN_PARAMS.every((param) => {
      return params.hasOwnProperty(param);
    });
    return hasAllParams;
  }, [params, isSsoRegType]);

  const testForSsoUc2Tokens = React.useCallback(() => {
    if (!isSsoRegType) return false;
    const hasAllParams = DEALS_SSO_UC2_TOKEN_PARAMS.every((param) => {
      return params.hasOwnProperty(param);
    });
    return hasAllParams;
  }, [params, isSsoRegType]);

  const testForSlaTokenParams = React.useCallback(() => {
    if (!isSlaTokenProgram) return false;
    const hasAllParams = SECURE_LINK_AUTH_TOKEN_PARAMS.every((param) => {
      return params.hasOwnProperty(param);
    });
    return hasAllParams;
  }, [params, isSlaTokenProgram]);

  const hasCvt = testForCvt();
  const hasDealsSsoUc1Tokens = testForSsoUc1Tokens();
  const hasDealsSsoUc2Tokens = testForSsoUc2Tokens();
  const hasSlaTokenParams = testForSlaTokenParams();

  // handles cvt auth, when needed
  const handleCvt = React.useCallback(() => {
    removeCookie(Member.CVT_SESSION);
    dispatch(Creators.authByCvt({ cvt: params.cvt }));
  }, [params, dispatch]);

  // handles sso use case one auth, when needed
  const handleSso = React.useCallback(
    (type) => {
      removeCookie(Member.CVT_SESSION);
      const ssoTokenObj = {
        type: type,
      };
      const paramsToSend = type === "2" ? DEALS_SSO_UC2_TOKEN_PARAMS : DEALS_SSO_UC1_TOKEN_PARAMS;
      paramsToSend.forEach((param) => {
        ssoTokenObj[param] = params[param];
      });
      if (
        process.env.REACT_APP_ENV !== "production" &&
        type === "2" &&
        params.hasOwnProperty("forceNewUser")
      ) {
        // dev/stage only param used to force new user creation for testing purposes
        ssoTokenObj["forceNewUser"] = `${params.forceNewUser}`;
      }
      dispatch(Creators.authBySsoToken(ssoTokenObj));
    },
    [params, dispatch],
  );

  // handles sla auth, when needed
  const handleSecureLinkAuth = React.useCallback(() => {
    removeCookie(Member.CVT_SESSION);
    const tokenSsoObj = {};
    SECURE_LINK_AUTH_TOKEN_PARAMS.forEach((param) => {
      tokenSsoObj[param] = params[param];
    });
    dispatch(Creators.authBySlaToken(tokenSsoObj));
  }, [params, dispatch]);

  // checks for an existing/valid session
  const handleSessionAuth = React.useCallback(() => {
    dispatch(Creators.validateSession());
  }, [dispatch]);

  // handle cvt auth when present, or validate/check for existing session
  React.useEffect(() => {
    if (!readyToContinue && memberAuthStatus === null) {
      if (isDirectorRoute && hasDealsSsoUc1Tokens) {
        handleSso("1");
      } else if (isDirectorRoute && hasDealsSsoUc2Tokens) {
        handleSso("2");
      } else if (isDirectorRoute && hasSlaTokenParams) {
        handleSecureLinkAuth();
      } else if (isDirectorRoute && hasCvt) {
        handleCvt();
      } else if (
        !isDirectorRoute ||
        (!hasCvt && !hasDealsSsoUc1Tokens && !hasDealsSsoUc2Tokens && !hasSlaTokenParams)
      ) {
        handleSessionAuth();
      }
    } else if (readyToContinueStatuses.includes(memberAuthStatus)) {
      setReadyToContinue(true);
    }
  }, [
    memberAuthStatus,
    isDirectorRoute,
    hasCvt,
    hasDealsSsoUc1Tokens,
    hasDealsSsoUc2Tokens,
    hasSlaTokenParams,
    readyToContinue,
    handleCvt,
    handleSso,
    handleSecureLinkAuth,
    handleSessionAuth,
    setReadyToContinue,
  ]);

  if (!readyToContinue) {
    return <Loading />;
  }
  return children;
};

export default CheckForSession;
